import { useState } from 'react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type {
  Stripe,
  StripeCardElementChangeEvent,
  StripeElements,
} from '@stripe/stripe-js'
import { useTranslation } from 'react-i18next'

import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import {
  ADD_CARD_TOAST_KEYS,
  useAddCardFields,
  useSaveCard,
} from './add-card-shared'
import type {
  AddCardFields,
  BillingAddressForm,
  TokenizedCard,
  UseAddCardArgs,
} from './add-card-shared'

const NAME_MAX_LENGTH = 100

export interface UseAddCardResult extends AddCardFields {
  cardError: string | undefined
  onCardChange: (event: StripeCardElementChangeEvent) => void
  canSubmit: boolean
  isSubmitting: boolean
  submit: () => Promise<void>
}

function cardholderName(fields: AddCardFields): string | undefined {
  const name = [fields.form.firstName.trim(), fields.form.lastName.trim()]
    .filter(Boolean)
    .join(' ')
    .slice(0, NAME_MAX_LENGTH)
  return name === '' ? undefined : name
}

function stripeAddress(billing: BillingAddressForm) {
  const address = {
    line1: billing.address.trim() || undefined,
    line2: billing.apartment.trim() || undefined,
    city: billing.city.trim() || undefined,
    state: billing.state.trim() || undefined,
    postal_code: billing.zip.trim() || undefined,
    country: billing.country.trim() || undefined,
  }
  const hasStreet = Object.values(address).some(
    (value) => value !== undefined && value !== 'US',
  )
  return hasStreet ? address : undefined
}

interface SubmitDeps {
  stripe: Stripe
  elements: StripeElements
  args: UseAddCardArgs
  fields: AddCardFields
  saveCard: (card: TokenizedCard) => Promise<void>
  t: ReturnType<typeof useTranslation>['t']
  setCardError: (next: string | undefined) => void
}

async function submitAddCard(deps: SubmitDeps): Promise<void> {
  const { stripe, elements, fields, t, setCardError } = deps
  const card = elements.getElement(CardElement)
  if (!card) return
  const result = await stripe.createPaymentMethod({
    type: 'card',
    card,
    billing_details: {
      name: cardholderName(fields),
      address: stripeAddress(fields.billing),
    },
  })
  if (result.error) {
    setCardError(result.error.message)
    toast.error(result.error.message ?? t(ADD_CARD_TOAST_KEYS.error))
    return
  }
  const details = result.paymentMethod.card
  if (!details) {
    toast.error(t(ADD_CARD_TOAST_KEYS.error))
    return
  }
  await deps.saveCard({
    paymentToken: result.paymentMethod.id,
    expMonth: details.exp_month,
    expYear: details.exp_year,
  })
}

export function useAddCard(args: UseAddCardArgs): UseAddCardResult {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const fields = useAddCardFields()
  const { save, isSaving } = useSaveCard()
  const [cardComplete, setCardComplete] = useState(false)
  const [cardError, setCardError] = useState<string | undefined>(undefined)
  const [isTokenizing, setIsTokenizing] = useState(false)

  const onCardChange = (event: StripeCardElementChangeEvent) => {
    setCardComplete(event.complete)
    setCardError(event.error?.message)
  }

  const isSubmitting = isTokenizing || isSaving
  const canSubmit =
    Boolean(stripe) &&
    Boolean(elements) &&
    cardComplete &&
    Boolean(args.customerId) &&
    !isSubmitting

  const submit = async () => {
    if (!stripe || !elements || !canSubmit) return
    setIsTokenizing(true)
    try {
      await submitAddCard({
        stripe,
        elements,
        args,
        fields,
        saveCard: (card) => save(args, fields, card),
        t,
        setCardError,
      })
    } catch (err) {
      toast.error(getApiErrorDetail(err) ?? t(ADD_CARD_TOAST_KEYS.error))
    } finally {
      setIsTokenizing(false)
    }
  }

  return {
    ...fields,
    cardError,
    onCardChange,
    canSubmit,
    isSubmitting,
    submit,
  }
}
